import {Button} from '@/components/MLS/Button/Button';
import {Modal} from '@/components/Modal/Modal';
import {PlayerProfileForm, UserProfileFormInterface} from '@/components/PlayerProfileForm/PlayerProfileForm';
import {useCreateProfile} from '@/hooks/useCreateProfile';
import {isPlayer} from '@/user/user-utils';
import {useQueryClient} from '@tanstack/react-query';
import {memo, useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {markProfileInactive} from '../../api/api';
import {useProfiles} from '../../api/User/useProfiles';
import CloseIcon from '../../assets/img/mls/close-icon.svg?react';
import {useTrainerContext} from '../../context/UserContextProvider';
import {useGetMboClientStatus} from '../../hooks/useGetMboClientStatus';
import {useLogout} from '../../hooks/useLogout';
import {Profile} from '../../user/player-info.interface';
import {AddUpdatePhoneNumber} from './AddUpdatePhoneNumber';
import styles from './ProfileModal.module.scss';

export const ProfileModal = memo(({isOpen, setClosed}: {isOpen: boolean; setClosed: () => void}) => {
  const navigate = useNavigate();
  const trainerContext = useTrainerContext();
  const {logout} = useLogout();
  const {currentProfile, otherProfiles} = useProfiles();
  const {isInactive} = useGetMboClientStatus();
  const [isCreateProfileModalOpen, setIsCreateProfileModalOpen] = useState(false);
  const {createProfile, isLoading: isProfileCreationLoading} = useCreateProfile();
  const [profileCreationErrorMessage, setProfileCreationErrorMessage] = useState('');
  const logoutClick = useCallback(() => {
    setClosed();
    logout();
  }, [logout, setClosed]);

  const profileClick = useCallback(() => {
    setClosed();
    navigate('/profile');
  }, [navigate, setClosed]);

  const endEmulation = useCallback(() => {
    setClosed();
    if (trainerContext) {
      trainerContext.endEmulateUser();
      navigate('/trainer');
    }
  }, [navigate, setClosed, trainerContext]);

  const handleCreateProfile = async (data: UserProfileFormInterface) => {
    try {
      await createProfile(data);
      setIsCreateProfileModalOpen(false);
    } catch (error) {
      console.error('Error creating profile:', error);
      setProfileCreationErrorMessage('Something went wrong, please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={setClosed} title="Profiles" disableBackgroundClick={true}>
      <Modal
        isOpen={isCreateProfileModalOpen}
        onClose={() => setIsCreateProfileModalOpen(false)}
        title="Create New Profile"
        showHeader
        modalTestId="create-new-profile-modal"
        disableBackgroundClick={true}
        closeButtonTestId="create-profile-close-modal-buton"
      >
        <PlayerProfileForm
          isLoading={isProfileCreationLoading}
          onSubmit={handleCreateProfile}
          errorMessage={profileCreationErrorMessage}
        />
      </Modal>
      <div className={styles.container} data-test-id="profile-modal-container">
        <button className="absolute right-0 top-0" onClick={setClosed} data-test-id="profile-modal-close-icon">
          <CloseIcon />
        </button>
        <div className={styles.profileWrapper}>
          <div className={styles.profile}>
            <div className={styles.flexRow}>
              <p data-test-id="current-profile-name" className={styles.name}>
                {currentProfile?.firstName} {currentProfile?.lastName}
              </p>
              {isInactive(currentProfile?.mboDetails?.clientId) ? (
                <p className={styles.inactiveLabel}>inactive</p>
              ) : null}
            </div>
            {currentProfile.email && <p>{currentProfile.email}</p>}
            <p data-test-id="current-profile-location" className={styles.location}>
              {currentProfile?.location?.name}
            </p>
            <AddUpdatePhoneNumber />
          </div>
          {/* <Button variant="dark" size="sm" label="My Profile" onClick={profileClick} /> */}
        </div>

        {otherProfiles.length > 0 ? (
          <>
            <h4 className={styles.subheading}>Switch to a different profile below</h4>
            <ul>
              {otherProfiles.map((profile, i) => (
                <ProfileCard profile={profile} key={`profilecard${i}`} setClosed={setClosed} />
              ))}
            </ul>
          </>
        ) : null}

        {isPlayer(currentProfile) && (
          <div className={styles.buttonContainer}>
            <Button
              variant="dark"
              size="sm"
              label="Create New Profile"
              data-test-id="create-new-profile-button"
              onClick={() => setIsCreateProfileModalOpen(true)}
            />
          </div>
        )}

        {trainerContext && trainerContext.trainer.accountId !== currentProfile.accountId ? (
          <div className={styles.buttonContainer}>
            {/* <StandardButton variant="primary" text="End User Emulation" onClick={endEmulation} /> */}
            <Button variant="dark" size="sm" label="End User Emulation" onClick={endEmulation} />
          </div>
        ) : (
          <div className={styles.buttonContainer}>
            {/* <StandardButton variant="light" text="Log Out" onClick={logoutClick} /> */}
            <Button data-test-id="logout-button" variant="dark" size="sm" label="Log Out" onClick={logoutClick} />
          </div>
        )}
      </div>
    </Modal>
  );
});
ProfileModal.displayName = 'ProfileModal';

const ProfileCard = memo(({profile, setClosed}: {profile: Profile; setClosed: () => void}) => {
  const queryClient = useQueryClient();
  const {selectProfile} = useProfiles();
  const {isInactive} = useGetMboClientStatus();
  const [deleteIntent, setDeleteIntent] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const onClick = useCallback(() => {
    selectProfile(profile._id);
    setClosed();
  }, [profile._id, selectProfile, setClosed]);

  const handleDelete = useCallback(async (profileId: string) => {
    try {
      setIsDeleting(true);
      await markProfileInactive(profileId);
      await queryClient.invalidateQueries({queryKey: ['user'], refetchType: 'all'});
      setClosed();
    } catch (error) {
      console.error('Error deleting profile:', error);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  if (isDeleted) {
    return null;
  }

  if (deleteIntent) {
    return (
      <li className={styles.cardContainer}>
        <div className={`${styles.profileWrapper} !flex-col`}>
          <p className="text-lg font-semibold">
            {profile.firstName} {profile.lastName}
          </p>
          <div className="text-sm">{profile.location?.name}</div>
          <div className="text-sm font-semibold mt-3">Are you sure you want to delete this profile?</div>
          <div className="flex items-center justify-between mt-3 w-full">
            <Button
              variant="default"
              label={isDeleting ? 'Deleting...' : 'Delete'}
              onClick={() => handleDelete(profile._id)}
              className="!bg-red-500 text-white"
              disabled={isDeleting}
            />
            <Button variant="default" label="Cancel" onClick={() => setDeleteIntent(false)} disabled={isDeleting} />
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className={styles.cardContainer} onClick={onClick}>
      <div className={styles.profileWrapper}>
        <div className={styles.profile} data-test-id={`profile-card-${profile.firstName}-${profile.lastName}`}>
          <div className={styles.flexRow}>
            <p data-test-id={`profile-name`} className={styles.name}>
              {profile.firstName} {profile.lastName}
            </p>
            {isInactive(profile.mboDetails?.clientId) ? <p className={styles.inactiveLabel}>inactive</p> : null}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm">{profile.location?.name}</div>
            <Button
              data-test-id={`profile-delete-button-${profile.firstName}-${profile.lastName}`}
              variant="default"
              size="sm"
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteIntent(true);
              }}
            />
          </div>
        </div>
      </div>
    </li>
  );
});
ProfileCard.displayName = 'ProfileCard';
